import React, { useState, useCallback, useEffect } from 'react';
import { Map, fromJS } from 'immutable';
import camelCase from 'lodash/camelCase';

// Types
import { FormTypeType, ReviewFormTemplateType } from 'Shared/types/ReviewFormTemplate.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// API
import * as api from 'Shared/api/ReviewTemplates.api';

// Components
import { WpPageLayout, WpPageBody, WpPageUtilityNavBar } from 'Wpds/components/page-layout';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpSteps, WpStep } from 'Wpds/components/steps-v2';
import QuestionnaireStep from 'Dashboard/components/ThreeSixtyReviewQuestionnairePage/QuestionnaireStep/QuestionnaireStep';
import PageReviewHeader from '../ThreeSixtyReviewPartials/ReviewsPageHeader/ReviewsPageHeader';
import ReviewStep from 'Dashboard/components/ThreeSixtyReviewQuestionnairePage/ReviewStep/ReviewStep';
import WpSelectV2 from 'Wpds/components/select/WpSelectV2';
import { WpText } from 'Wpds/components/text';

// Styles
import 'Dashboard/components/ThreeSixtyReviewQuestionnairePage/ThreeSixtyReviewQuestionnairePage.scss';
import './ReviewTemplatePreviewContent.scss';

type Props = {
  reviewTemplateSlug: string;
  handleExitClick: () => void;
  isPerformaneReviewTemplate?: boolean;
};

const ReviewTemplatePreviewContent = ({
  reviewTemplateSlug,
  handleExitClick,
  isPerformaneReviewTemplate = false,
}: Props) => {
  const [activeStep, setActiveStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState(null);
  const [reviewFormTemplates, setReviewFormTemplates] = useState<ReviewFormTemplateType[]>(null);
  const [selectedTemplateType, setSelectedTemplateType] = useState<FormTypeType>(null);
  const [isSwitchingForm, toggleIsSwitchingForm] = useToggle(false);

  const { user: currentUser } = useCurrentUser();
  const currentUserFullName = currentUser.full_name;
  const currentUserAvatar = currentUser.avatar;

  const getPreviewOptionsLabel = (formType: FormTypeType) => {
    switch (formType) {
      case FormTypeType.self_peer_review:
        return 'Self';
      case FormTypeType.peer_review:
        return 'Peer';
      case FormTypeType.manager_peer_review:
        return 'Manager';
      default:
        return 'Questions';
    }
  };

  const getTitle = () => {
    const type = isPerformaneReviewTemplate ? 'Review' : 'Feedback';

    if (selectedTemplateType === FormTypeType.self_peer_review) return `Self ${type}`;
    else return `${type} for ${currentUserFullName} (Example)`;
  };

  const camelCaseKeys = object => {
    return Object.entries(object).reduce((carry, [key, value]) => {
      carry[camelCase(key)] = value;
      return carry;
    }, {});
  };

  useEffect(() => {
    // Passing null as the argument means use the default review template
    api.fetchReviewTemplate((reviewTemplateSlug || 'default') as string).then(response => {
      setName(response.data.name);

      const templates = response.data.reviewFormTemplates
        .filter(template => template.form_type !== FormTypeType.compilation)
        .map(template => {
          return { formType: template.form_type, questions: template.questions.map(q => camelCaseKeys(q)) };
        });

      setReviewFormTemplates(templates);
      setSelectedTemplateType(templates[0].formType);
    });
  }, []);

  const moveToNextStep = useCallback(() => {
    setActiveStep(activeStep + 1);
  }, [activeStep]);

  const moveToPreviousStep = useCallback(() => {
    setActiveStep(activeStep - 1);
  }, [activeStep]);

  const handleSetAnswers = questionData => {
    setAnswers({ ...answers, ...questionData });
  };

  const handleSetSelectedTemplateType = option => {
    toggleIsSwitchingForm();
    setSelectedTemplateType(option);
    setAnswers({});
    setActiveStep(1);
    setTimeout(() => toggleIsSwitchingForm(), 10);
  };

  const QuestionnairePageBaseClassName = 'ThreeSixtyReviewQuestionnairePage';
  const baseClassName = 'ReviewTemplatePreviewContent';

  if (!reviewFormTemplates) return null;
  if (isSwitchingForm) return null;

  const selectedTemplate =
    selectedTemplateType &&
    Map(fromJS(camelCaseKeys(reviewFormTemplates.find(t => t.formType === selectedTemplateType))));

  return (
    <WpPageLayout width="container" className={QuestionnairePageBaseClassName}>
      <WpPageUtilityNavBar
        className={`${baseClassName}__utility-bar`}
        width="full"
        wrapperClassName={`${baseClassName}__utility-bar__wrapper`}
      >
        <WpText color="white-0" noMargin>
          <WpText bold noMargin tag="span" color="white-0">
            Template:&nbsp;
          </WpText>
          {name}
        </WpText>
        {reviewFormTemplates.length > 1 ? (
          <div className={`${baseClassName}__utility-bar__select`}>
            <div className={`${baseClassName}__utility-bar__select__label`}>
              <WpText bold noMargin color="white-0">
                Preview as:
              </WpText>
            </div>
            <WpSelectV2
              id="select_template_type"
              label="Preview as"
              value={selectedTemplateType}
              onChange={handleSetSelectedTemplateType}
              labelHidden
              options={reviewFormTemplates.map(rft => {
                return { value: rft.formType, label: getPreviewOptionsLabel(rft.formType) };
              })}
            />
          </div>
        ) : (
          // Empty div for flex alignment
          <div></div>
        )}
        <div className={`${baseClassName}__utility-bar__exit-btn`}>
          <WpButton onClick={handleExitClick} color="white" variant="tertiary">
            <WpIcon name="times" />
            Exit Preview
          </WpButton>
        </div>
      </WpPageUtilityNavBar>

      <PageReviewHeader sticky={false} title={getTitle()} avatar={currentUserAvatar} />
      <WpPageBody>
        <div className={`${QuestionnairePageBaseClassName}__content`}>
          <div className={`${QuestionnairePageBaseClassName}__content__steps`}>
            <WpSteps direction="vertical" activeStepNumber={activeStep}>
              <WpStep title={isPerformaneReviewTemplate ? 'Write review' : 'Questionnaire'} />
              <WpStep title="Review & Submit" />
            </WpSteps>
          </div>
          {!selectedTemplate && <div>loading...</div>}
          {selectedTemplate && (
            <div className={`${QuestionnairePageBaseClassName}__content__main`}>
              {activeStep === 1 && (
                <QuestionnaireStep
                  review={null}
                  peerReviewForm={selectedTemplate}
                  answers={answers}
                  formData={{}}
                  handleSetAnswers={handleSetAnswers}
                  moveToNextStep={moveToNextStep}
                  isPreview
                />
              )}
              {activeStep === 2 && (
                <ReviewStep
                  peerReviewForm={selectedTemplate}
                  answers={answers}
                  formData={{}}
                  handleBackClick={moveToPreviousStep}
                  handleSubmitReview={null}
                  isPreview
                  isAlreadySubmitted={false}
                />
              )}
            </div>
          )}
        </div>
      </WpPageBody>
    </WpPageLayout>
  );
};

export default ReviewTemplatePreviewContent;
